// Synthesized button + arrival cues. Fails silent if Web Audio is unavailable
// (SSR, jsdom, locked-down browsers) so callers never need to guard.

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let lifecycleBound = false;

// The iOS ring/silent switch latches onto whatever state the AudioContext had
// at creation time. Dropping the context when the page is shown again forces
// the next sound to build a fresh one reflecting the current switch position,
// so users don't have to reload the page after toggling silent.
function disposeCtx(): void {
  if (!ctx) return;
  ctx.close().catch(() => {});
  ctx = null;
  master = null;
}

function bindLifecycle(): void {
  if (lifecycleBound || typeof document === 'undefined') return;
  lifecycleBound = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') disposeCtx();
  });
  window.addEventListener('pageshow', disposeCtx);
}

function ensureCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (ctx) {
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    return ctx;
  }
  const C: typeof AudioContext | undefined =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!C) return null;
  try {
    ctx = new C();
    bindLifecycle();
    master = ctx.createGain();
    master.gain.value = 0.85;
    master.connect(ctx.destination);
    // iOS creates the context suspended; the first caller is a pointerdown
    // handler, so resuming here unlocks audio within the user gesture.
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    return ctx;
  } catch {
    ctx = null;
    master = null;
    return null;
  }
}

/** Short pop on button press. ~50ms, exponential pitch drop. */
export function playClick(): void {
  const c = ensureCtx();
  if (!c || !master) return;
  try {
    const t0 = c.currentTime;
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, t0);
    osc.frequency.exponentialRampToValueAtTime(220, t0 + 0.04);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.55, t0 + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.06);
    osc.connect(g).connect(master);
    osc.start(t0);
    osc.stop(t0 + 0.07);
  } catch { /* fail silent */ }
}

let scrollNoiseBuf: AudioBuffer | null = null;
function ensureScrollNoise(c: AudioContext): AudioBuffer {
  if (scrollNoiseBuf && scrollNoiseBuf.sampleRate === c.sampleRate) return scrollNoiseBuf;
  const len = Math.floor(0.04 * c.sampleRate);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const ch = buf.getChannelData(0);
  for (let i = 0; i < len; i += 1) ch[i] = Math.random() * 2 - 1;
  scrollNoiseBuf = buf;
  return buf;
}

/** Mouse-wheel detent click — noise body + tonal "thock" (~30ms). */
export function playScrollTick(): void {
  const c = ensureCtx();
  if (!c || !master) return;
  try {
    const t0 = c.currentTime;

    // Noise body: bandpass for the "tk" texture, jittered per tick.
    const n = c.createBufferSource();
    n.buffer = ensureScrollNoise(c);
    const bp = c.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1100 + Math.random() * 300;
    bp.Q.value = 1.6;
    const ng = c.createGain();
    ng.gain.setValueAtTime(0.0001, t0);
    ng.gain.exponentialRampToValueAtTime(0.14, t0 + 0.002);
    ng.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.03);
    n.connect(bp).connect(ng).connect(master);
    n.start(t0);
    n.stop(t0 + 0.05);

    // Tonal punch: quick pitch drop gives the "ka" attack.
    const o = c.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(720, t0);
    o.frequency.exponentialRampToValueAtTime(380, t0 + 0.015);
    const og = c.createGain();
    og.gain.setValueAtTime(0.0001, t0);
    og.gain.exponentialRampToValueAtTime(0.23, t0 + 0.003);
    og.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.028);
    o.connect(og).connect(master);
    o.start(t0);
    o.stop(t0 + 0.04);
  } catch { /* fail silent */ }
}

/** Two-note arrival cue when an item lands in the cart. ~200ms. */
export function playDing(): void {
  const c = ensureCtx();
  if (!c || !master) return;
  try {
    const t0 = c.currentTime;
    [880, 1318.5].forEach((freq, i) => {
      const start = t0 + i * 0.06;
      const osc = c.createOscillator();
      const g = c.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, start);
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.8, start + 0.005);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 0.18);
      osc.connect(g).connect(master!);
      osc.start(start);
      osc.stop(start + 0.2);
    });
  } catch { /* fail silent */ }
}
